import { useState, useEffect, useCallback } from "react";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  limit,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../config/firebase";
import { firebaseOptimizer, isTabVisible } from "../services/firebaseOptimizer";

/**
 * Custom hook for items management operations
 */
export const useItems = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to convert Firestore timestamps to readable dates
  const formatFirestoreDate = useCallback((timestamp) => {
    if (!timestamp) return "לא זמין";
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleString("he-IL");
    }
    if (timestamp instanceof Date) {
      return timestamp.toLocaleString("he-IL");
    }
    try {
      return new Date(timestamp).toLocaleString("he-IL");
    } catch (error) {
      return "לא זמין";
    }
  }, []);

  // Optimized items listener with caching
  useEffect(() => {
    // Check cache first
    const cachedData = firebaseOptimizer.getCachedData("items");
    if (cachedData) {
      setItems(cachedData);
      setLoading(false);
      return;
    }

    // Skip if tab not visible
    if (!isTabVisible()) {
      setLoading(false);
      return;
    }

    // Load from Firebase - ALL items (no limit)
    const itemsRef = collection(db, "items");
    const q = query(itemsRef, orderBy("createdAt", "desc"));

    // Use connection management to prevent duplicate listeners
    const unsubscribe = firebaseOptimizer.getListener(
      "items",
      q,
      (snapshot) => {
        firebaseOptimizer.trackRead(snapshot.docs.length);

        const itemsData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            itemName: data.itemName || "",
            isChecked: Boolean(data.isChecked),
            note: data.note || "",
            makat: data.makat || "",
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
          };
        });

        firebaseOptimizer.setCachedData("items", itemsData);
        setItems(itemsData);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Add item with optimized performance
  const addItem = async (itemData) => {
    try {
      // Generate unique item ID
      const newItemRef = doc(collection(db, "items"));
      const newItem = {
        ...itemData,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      await setDoc(newItemRef, newItem);
      firebaseOptimizer.clearCache("items"); // Invalidate cache on write
      return { success: true };
    } catch (e) {
      console.error("Error adding item: ", e);
      return { success: false, error: e.message };
    }
  };

  // Update item with optimized performance
  const updateItem = async (id, updatedData) => {
    try {
      const itemRef = doc(db, "items", id);
      await updateDoc(itemRef, { ...updatedData, updatedAt: new Date() });
      firebaseOptimizer.clearCache("items"); // Invalidate cache on write
      return { success: true };
    } catch (e) {
      console.error("Error updating item: ", e);
      return { success: false, error: e.message };
    }
  };

  // Delete item with optimized performance
  const deleteItem = async (id) => {
    try {
      await deleteDoc(doc(db, "items", id));
      firebaseOptimizer.clearCache("items"); // Invalidate cache on write
      return { success: true };
    } catch (e) {
      console.error("Error deleting item: ", e);
      return { success: false, error: e.message };
    }
  };

  return {
    items,
    loading,
    error,
    addItem,
    updateItem,
    deleteItem,
    formatFirestoreDate,
  };
};
